/**
 * Sunday Clovr reference gallery controller.
 *
 * Thumbnail buttons replace only the large image, matching the approved
 * prototype while the two detail frames stay in place. Zoom delegates to
 * Horizon's native media-gallery/zoom-dialog components.
 */
class ScPdpGallery extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('pointerenter', this.#handlePointerEnter, true);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('pointerenter', this.#handlePointerEnter, true);
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const thumbnail = event.target.closest('[data-sc-thumb]');
    if (thumbnail instanceof HTMLButtonElement) {
      this.#selectThumbnail(thumbnail);
      return;
    }

    const zoomTarget = event.target.closest('[data-sc-zoom-index], [data-sc-main-zoom]');
    if (!(zoomTarget instanceof HTMLButtonElement)) return;

    const index = zoomTarget.hasAttribute('data-sc-main-zoom')
      ? Number(this.dataset.activeIndex || 0)
      : Number(zoomTarget.dataset.scZoomIndex || 0);
    this.#openZoom(index, event);
  };

  #handlePointerEnter = (event) => {
    if (!(event.target instanceof Element)) return;
    const thumbnail = event.target.closest('[data-sc-thumb]');
    if (!(thumbnail instanceof HTMLButtonElement)) return;

    const index = Number(thumbnail.dataset.scIndex);
    if (!Number.isInteger(index)) return;
    this.#mediaGallery?.preloadImage(index);
  };

  /** @param {HTMLButtonElement} thumbnail */
  #selectThumbnail(thumbnail) {
    const index = Number(thumbnail.dataset.scIndex);
    if (!Number.isInteger(index) || index < 0) return;

    const template = this.querySelector(`template[data-sc-main-template="${index}"]`);
    const main = this.querySelector('[data-sc-main-zoom]');
    if (!(template instanceof HTMLTemplateElement) || !(main instanceof HTMLButtonElement)) return;

    const nextImage = template.content.firstElementChild?.cloneNode(true);
    if (!(nextImage instanceof HTMLImageElement)) return;

    const oldImage = main.querySelector('img');
    if (oldImage) oldImage.replaceWith(nextImage);
    else main.prepend(nextImage);

    this.dataset.activeIndex = String(index);
    main.dataset.scZoomIndex = String(index);
    main.setAttribute('aria-label', `View ${thumbnail.dataset.scLabel || `image ${index + 1}`} larger`);

    const caption = this.querySelector('[data-sc-active-caption]');
    const captionLabel = thumbnail.dataset.scCaption || `Image ${index + 1}`;
    if (caption) caption.textContent = `${captionLabel} · Click to enlarge`;

    for (const button of this.querySelectorAll('[data-sc-thumb]')) {
      button.setAttribute('aria-pressed', String(button === thumbnail));
    }
  }

  /**
   * @param {number} index
   * @param {Event} event
   */
  #openZoom(index, event) {
    if (!Number.isInteger(index) || index < 0) return;
    this.#mediaGallery?.zoom(index, event);
  }

  get #mediaGallery() {
    return this.closest('media-gallery');
  }
}

if (!customElements.get('sc-pdp-gallery')) {
  customElements.define('sc-pdp-gallery', ScPdpGallery);
}
